import React, { FormEvent, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import api from '../services/api';
import { iniciarSessao } from '../services/sessao';
import './SaasPortal.css';

export default function SaasRegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [rgUf, setRgUf] = useState('');
  const [emailLocal, setEmailLocal] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirma, setMostrarConfirma] = useState(false);

  // ⚠️ SEGURANÇA CRÍTICA: Apenas emails @gestaocreche.com.br podem registrar no SaaS admin
  const DOMINIO_ADMIN_OBRIGATORIO = '@gestaocreche.com.br';

  // Limpar erro quando usuário começa a digitar
  function handleInputChange(setter: (value: string) => void, value: string) {
    if (error) setError('');
    setter(value);
  }

  // Formatar CPF
  function formatarCpf(v: string) {
    return v.replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4')
      .slice(0, 14);
  }

  // Validar CPF básico
  function validarCpf(cpfValue: string): string | null {
    const cpfLimpo = cpfValue.replace(/\D/g, '');
    if (!cpfLimpo) {
      return 'CPF é obrigatório.';
    }
    if (cpfLimpo.length !== 11) {
      return 'CPF deve ter 11 dígitos.';
    }
    return null;
  }

  function validarEmail(emailValue: string): string | null {
    if (!emailValue.trim()) {
      return 'E-mail é obrigatório.';
    }
    const emailCompleto = emailValue.trim().toLowerCase() + DOMINIO_ADMIN_OBRIGATORIO;
    if (!emailCompleto.includes('@')) {
      return 'E-mail inválido.';
    }
    return null;
  }

  function validarSenha(senhaValue: string): string | null {
    if (!senhaValue) {
      return 'Senha é obrigatória.';
    }
    if (senhaValue.length < 8) {
      return 'Senha deve ter no mínimo 8 caracteres.';
    }
    if (!/[A-Z]/.test(senhaValue)) {
      return 'Senha deve conter pelo menos uma letra maiúscula.';
    }
    if (!/[0-9]/.test(senhaValue)) {
      return 'Senha deve conter pelo menos um número.';
    }
    return null;
  }

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!nome.trim()) {
      setError('Nome completo é obrigatório.');
      return;
    }

    const erroCpf = validarCpf(cpf);
    if (erroCpf) {
      setError(erroCpf);
      return;
    }

    if (!rg.trim()) {
      setError('RG é obrigatório.');
      return;
    }

    if (!rgUf || rgUf.length !== 2) {
      setError('Estado de emissão do RG é obrigatório.');
      return;
    }

    const erroEmail = validarEmail(emailLocal);
    if (erroEmail) {
      setError(erroEmail);
      return;
    }

    // ⚠️ Validação de Segurança: rejeita qualquer email que não seja do domínio correto
    const emailCompleteValidacao = (emailLocal.trim().toLowerCase() + DOMINIO_ADMIN_OBRIGATORIO);
    if (!emailCompleteValidacao.endsWith(DOMINIO_ADMIN_OBRIGATORIO)) {
      setError(`❌ Acesso negado. Apenas emails ${DOMINIO_ADMIN_OBRIGATORIO} podem registrar no SaaS.`);
      return;
    }

    const erroSenha = validarSenha(senha);
    if (erroSenha) {
      setError(erroSenha);
      return;
    }

    if (senha !== confirmaSenha) {
      setError('As senhas não conferem.');
      return;
    }

    setLoading(true);

    try {
      const emailCompleto = (emailLocal.trim().toLowerCase() + DOMINIO_ADMIN_OBRIGATORIO);

      // Registrar nova conta
      await api.post('/auth/saas-register', {
        nome: nome.trim(),
        cpf: cpf.replace(/\D/g, ''),
        rg: rg.trim(),
        rg_uf: rgUf.toUpperCase(),
        email: emailCompleto,
        senha,
      });

      // Fazer login automático com as credenciais registradas
      const loginResponse = await api.post('/auth/login', {
        email: emailCompleto,
        senha,
      });

      // Sessão do painel SaaS desta aba (não toca na sessão da creche).
      if (loginResponse.data?.token) {
        iniciarSessao(loginResponse.data.token, 'saas');
      }

      setSuccess('✅ Cadastro realizado com sucesso! Redirecionando para o dashboard em 2 segundos...');
      setNome('');
      setCpf('');
      setRg('');
      setRgUf('');
      setEmailLocal('');
      setSenha('');
      setConfirmaSenha('');
      setTimeout(() => navigate('/saas/dashboard'), 2000);
    } catch (err) {
      if (isAxiosError(err) && !err.response) {
        setError('❌ Servidor indisponível. Tente novamente mais tarde.');
      } else if (isAxiosError(err) && err.response?.data) {
        setError('❌ ' + ((err.response.data as any).message || 'Falha ao registrar. Verifique os dados e tente novamente.'));
      } else {
        setError('❌ Erro ao registrar administrador. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="saas-login-page">
      <div className="saas-login-bg">
        <div className="saas-login-orb saas-login-orb-1" />
        <div className="saas-login-orb saas-login-orb-2" />
        <div className="saas-login-grid-bg" />
      </div>

      <div className="saas-login-container">
        <div className="saas-login-card saas-register-card">
          <header className="saas-login-header">
            <div className="saas-logo-icon">💠</div>
            <h1>Registrar <strong>Administrador</strong></h1>
            <p>Crie sua conta SaaS — Alencastro Consultoria</p>
          </header>

          <form className="saas-login-form" onSubmit={handleRegister} noValidate>
            {/* Aviso de domínio de email */}
            <div className="saas-register-info">
              <span className="saas-info-icon">ℹ️</span>
              <p>
                Apenas administradores com e-mail <strong>{DOMINIO_ADMIN_OBRIGATORIO}</strong> podem criar contas neste sistema.
              </p>
            </div>

            {error && (
              <div className="saas-login-error" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="saas-register-success" role="alert">
                {success}
              </div>
            )}

            {/* Nome Completo */}
            <div className="saas-login-field">
              <label htmlFor="nome">Nome completo <span className="saas-required">*</span></label>
              <div className="saas-input-wrapper">
                <span className="saas-input-icon">👤</span>
                <input
                  id="nome"
                  type="text"
                  placeholder="João Silva"
                  value={nome}
                  onChange={(e) => handleInputChange(setNome, e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* CPF */}
            <div className="saas-login-field">
              <label htmlFor="cpf">CPF <span className="saas-required">*</span></label>
              <div className="saas-input-wrapper">
                <span className="saas-input-icon">🆔</span>
                <input
                  id="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => handleInputChange(setCpf, formatarCpf(e.target.value))}
                  inputMode="numeric"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* RG */}
            <div className="saas-login-field-row">
              <div className="saas-login-field saas-field-flex">
                <label htmlFor="rg">RG <span className="saas-required">*</span></label>
                <div className="saas-input-wrapper">
                  <span className="saas-input-icon">📋</span>
                  <input
                    id="rg"
                    type="text"
                    placeholder="12345678"
                    value={rg}
                    onChange={(e) => handleInputChange(setRg, e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="saas-login-field saas-field-flex">
                <label htmlFor="rgUf">Estado <span className="saas-required">*</span></label>
                <div className="saas-input-wrapper">
                  <span className="saas-input-icon">🗺️</span>
                  <input
                    id="rgUf"
                    type="text"
                    placeholder="SP"
                    maxLength={2}
                    value={rgUf}
                    onChange={(e) => handleInputChange(setRgUf, e.target.value.toUpperCase())}
                    disabled={loading}
                    required
                  />
                </div>
              </div>
            </div>

            {/* E-mail */}
            <div className="saas-login-field">
              <label htmlFor="emailLocal">
                E-mail administrativo <span className="saas-required">*</span>
              </label>
              <div className="saas-input-wrapper saas-email-wrapper">
                <span className="saas-input-icon">✉️</span>
                <input
                  id="emailLocal"
                  type="text"
                  placeholder="seu-nome"
                  value={emailLocal}
                  onChange={(e) => handleInputChange(setEmailLocal, e.target.value)}
                  disabled={loading}
                  required
                />
                <span className="saas-email-domain">{DOMINIO_ADMIN_OBRIGATORIO}</span>
              </div>
              <small className="saas-field-hint">
                Você receberá: <strong>{emailLocal || 'seu-nome'}{DOMINIO_ADMIN_OBRIGATORIO}</strong>
              </small>
            </div>

            {/* Senha */}
            <div className="saas-login-field">
              <label htmlFor="senha">Senha <span className="saas-required">*</span></label>
              <div className="saas-input-wrapper saas-input-com-toggle">
                <span className="saas-input-icon">🔒</span>
                <input
                  id="senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={senha}
                  onChange={(e) => handleInputChange(setSenha, e.target.value)}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="saas-toggle-button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  disabled={loading}
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                >
                  {mostrarSenha ? '🙈' : '👁️'}
                </button>
              </div>
              <small className="saas-field-hint">
                Mínimo 8 caracteres, com letra maiúscula e número.
              </small>
            </div>

            {/* Confirmar Senha */}
            <div className="saas-login-field">
              <label htmlFor="confirmaSenha">Confirmar senha <span className="saas-required">*</span></label>
              <div className="saas-input-wrapper saas-input-com-toggle">
                <span className="saas-input-icon">🔒</span>
                <input
                  id="confirmaSenha"
                  type={mostrarConfirma ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmaSenha}
                  onChange={(e) => handleInputChange(setConfirmaSenha, e.target.value)}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="saas-toggle-button"
                  onClick={() => setMostrarConfirma(!mostrarConfirma)}
                  disabled={loading}
                  aria-label={mostrarConfirma ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                >
                  {mostrarConfirma ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Botão de Registro */}
            <button
              type="submit"
              className="saas-login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="saas-spinner-small" />
                  Registrando...
                </>
              ) : (
                '✅ Criar Conta Administrativa'
              )}
            </button>
          </form>

          {/* Link para Login */}
          <div className="saas-register-footer">
            <p>
              Já possui uma conta?{' '}
              <Link to="/saas/login" className="saas-link-footer">
                Faça login aqui
              </Link>
            </p>
          </div>

          <footer className="saas-login-footer">
            <p>© {new Date().getFullYear()} GestãoCreche SaaS</p>
            <p>Infraestrutura de alta disponibilidade</p>
          </footer>
        </div>

        <div className="saas-login-info">
          <div className="saas-info-item">
            <h3>🛡️ Segurança de Elite</h3>
            <p>Monitoramento Proativo 24/7 e Logs de Auditoria Imutáveis.</p>
          </div>
          <div className="saas-info-item">
            <h3>🚀 Escalabilidade Global</h3>
            <p>Arquitetura Multi-Tenant com isolamento total de dados.</p>
          </div>
          <div className="saas-info-item">
            <h3>⚡ Performance Crítica</h3>
            <p>Baixa latência e alta consistência para gestão educacional.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
